import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { colors } from "@/constants/colors.jsx";

const collateralOptions = [
  "Commercial Real Estate",
  "Residential Real Estate",
  "Inventory",
  "Accounts Receivable",
  "Cash",
  "Investment Accounts",
  "New Equipment/Machinery",
  "Furniture & Fixtures",
  "Vehicles",
  "Used Equipment/Machinery",
  "Vacant Land",
  "Leasehold Improvements",
  "Other",
  "None"
];

const CollateralStep = ({ onNext, initialValue = [] }) => {
  const [selectedCollateral, setSelectedCollateral] = useState(initialValue);

  const handleCollateralChange = (value) => {
    if (value === "None") {
      // If "None" is selected, clear all other selections
      setSelectedCollateral(["None"]);
    } else {
      setSelectedCollateral(prev => {
        const newSelection = prev.includes(value)
          ? prev.filter(item => item !== value)
          : [...prev.filter(item => item !== "None"), value];
        return newSelection.length === 0 ? [] : newSelection;
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col space-y-6"
    >
      <div className="text-center space-y-2">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl md:text-3xl font-bold text-gray-900"
        >
          Do you have collateral to support the loan?
        </motion.h2>
        <motion.div 
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-12 h-0.5 mx-auto"
          style={{ backgroundColor: colors.primary[800] }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto"
        >
          Collateral is a business and/or personal assets that are able to be pledged as security for the loan. If you don't have any of the following, that's okay, just select "None".
        </motion.p>
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto w-full"
      >
        {collateralOptions.map((option, index) => (
          <motion.div
            key={option}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * index }}
            className={`
              flex items-center space-x-3 rounded-lg border border-slate-200 p-4 
              hover:bg-slate-50 hover:border-[#1a4a1a]/20 transition-all duration-200
              ${selectedCollateral.includes(option) ? 'bg-slate-50 border-[#1a4a1a]' : ''}
            `}
          >
            <Checkbox
              id={option}
              checked={selectedCollateral.includes(option)}
              onCheckedChange={() => handleCollateralChange(option)}
              className="data-[state=checked]:border-[#1a4a1a] data-[state=checked]:bg-[#1a4a1a]"
            />
            <Label
              htmlFor={option}
              className="text-base cursor-pointer flex-grow font-medium text-gray-700"
            >
              {option}
            </Label>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-auto pt-6"
      >
        <Button
          onClick={() => onNext(selectedCollateral)}
          disabled={selectedCollateral.length === 0}
          className="w-full text-white py-6 text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ 
            backgroundColor: colors.primary[800],
            '&:hover': {
              backgroundColor: `${colors.primary[800]}e6`
            }
          }}
        >
          Continue
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default CollateralStep; 